import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import '@/features/f0/styles/bodyFonts'
import { Button } from '@/features/f0/ui'
import { useAuthStore } from '@/stores/authStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { DEV_MODE } from '@/lib/devMode'
import '../u6.css'

export interface TrialBannerProps {
  className?: string
}

/**
 * Persistent banner for users approaching trial expiration.
 * Shows when is_trial and daysRemaining <= 8; dismissible with a 24h snooze.
 * Not shown for admins, subscribed (non-trial), or already-ended trials.
 */
export default function TrialBanner({ className }: TrialBannerProps) {
  const { user } = useAuthStore()
  const { dismissBanner, isBannerDismissed } = useNotificationStore()
  const navigate = useNavigate()

  if (DEV_MODE) return null
  if (!user || !user.is_trial) return null
  if (user.is_admin) return null
  if (user.trial_ended) return null // TrialExpiredOverlay handles this

  const daysRemaining = user.trial_days_remaining
  if (daysRemaining > 8) return null

  const bannerId = daysRemaining <= 2 ? 'trial-urgent' : daysRemaining <= 5 ? 'trial-warning' : 'trial-info'
  if (isBannerDismissed(bannerId)) return null

  const message =
    daysRemaining === 0
      ? 'Your trial expires today!'
      : daysRemaining === 1
        ? 'Your trial expires tomorrow!'
        : `Your trial ends in ${daysRemaining} days`

  return (
    <div className={['zupa-app', 'z-banner', className].filter(Boolean).join(' ')} role="status">
      <p>{message}</p>
      <Button variant="secondary" onClick={() => navigate('/settings/subscription')}>
        View Plans
      </Button>
      <button type="button" onClick={() => dismissBanner(bannerId)} aria-label="Dismiss banner">
        <X aria-hidden="true" />
      </button>
    </div>
  )
}
